import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpParams,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, from, throwError } from 'rxjs';
import { environment } from '@environments/environment';

import { Reserva, iReserva, ReservaRecurrente, iReservaRecurrente } from '@app/_models/reserva';
import { catchError } from 'rxjs/operators';
import { Match } from '@app/_models/match';
import { SessionService } from './session.service';
import { eReservetype } from '@app/_enums/reserve-type';

@Injectable({
  providedIn: 'root',
})
export class ReservasService {
  
  public refreshReservas: BehaviorSubject<boolean>;
  public refreshReservas$: Observable<boolean>;
  public idReserveEdit: number;
  private recargarModReservas = new Subject<void>();
  public shareCurrencyDay = new Date;

  constructor(
    private http: HttpClient,
    private _sessionService: SessionService
  ) {
    this.refreshReservas = new BehaviorSubject<boolean>(false);
    this.refreshReservas$ = this.refreshReservas.asObservable();
  }

  recargarModReservas$ = this.recargarModReservas.asObservable();

  notificarRecargaModReservas() {
    this.recargarModReservas.next();
  }

  getReservas(field, day: Date) {
    let url = `${environment.apiUrl}/reservations/list/field/${field}/`;
    if (day) {
      const yy = day.getFullYear();
      const mm = day.getMonth() + 1;
      const dd = day.getDate();
      url = url + `?y=${yy}&m=${mm}&d=${dd}`;
    }
    return this.http.get(url);
  }

  findReservas(
    facility_id,
    sortOrder = 'start_time',
    status = '',
    searchText = '',
    pageNumber,
    pageSize = 10
  ): Observable<Reserva[]> {
    let url = `${environment.apiUrl}/reservations/list/facility/${facility_id}/`;

    return this.http.get<Reserva[]>(url, {
      params: new HttpParams()
        .set('ordering', sortOrder)
        .set('pageNumber', pageNumber.toString())
        .set('pageSize', pageSize.toString())
        .set('searchText', searchText)
        .set('status', status),
    });
  }

  getClosedShifts(field, date: Date) {
    const facilityId = this._sessionService.currentFacility.id;
    const yy = date.getFullYear();
    const mm = date.getMonth() + 1;
    const dd = date.getDate();
    return this.http.get(
      `${environment.apiUrl}/reservations/list/field/${field}/closedshifts/facility/${facilityId}/${yy}/${mm}/${dd}/`
    );
  }

  getOpenShifts(facility, size, date: Date) {
    const yy = date.getFullYear();
    const mm = date.getMonth() + 1;
    const dd = date.getDate();
    return this.http.get(
      `${environment.apiUrl}/openshifts/${facility}/${yy}/${mm}/${dd}/${size}/`
    );
  }

  add(reserva: Reserva) {
    const url = reserva.reservation_type === eReservetype.Partidos
      ? `${environment.apiUrl}/reservations/`
      : `${environment.apiUrl}/reservations-extended/`;
  
    const i: iReserva = {
      facility: reserva.facility,
      planner: reserva.planner,
      field_capacity: reserva.field_capacity,
      start_time: this.toJSONLocal(reserva.start_time),
      end_time: this.toJSONLocal(reserva.end_time),
      name: reserva.name,
      phone: reserva.phone,
      reservation_type: reserva.reservation_type,
      description: reserva.description ? reserva.description : undefined,
      deposit_paid: false,
      ...(reserva.payway_payment && { payment_variable: reserva.payway_payment })
    };
    
    return this.http.post(url, i).pipe(catchError(this.handleError));
  }

  addRecurrente(reserva: ReservaRecurrente) {
    const i: iReservaRecurrente = {
      facility: reserva.facility,
      planner: reserva.planner,
      field_capacity: reserva.field_capacity,
      start_time: this.toJSONLocal(reserva.start_time),
      end_time: undefined,
      end_date: this.toJSONLocal(reserva.end_date),
      name: reserva.name,
      phone: reserva.phone,
      reservation_type: 2,
      selected_weekdays: `${JSON.stringify(reserva.selected_weekdays)}`,
      description: reserva.description ? reserva.description : undefined,
    };
    const url = `${environment.apiUrl}/recurring-reservations/`;
    return this.http.post(url, i).pipe(catchError(this.handleError));
  }

  updateReservaRecurrente(reserva: ReservaRecurrente) {
    const i: any = {
      phone: reserva.phone,
      name: reserva.name,
      description: reserva.description ? reserva.description : undefined,
      field_capacity: reserva.field_capacity.toString(),
      start_time: this.toJSONLocal(reserva.start_time),
      end_date: this.toJSONLocal(reserva.end_date),
      selected_weekdays: `${JSON.stringify(reserva.selected_weekdays)}`,
      reservation_group: reserva.reservation_group.toString()
    };
  
    const url = `${environment.apiUrl}/recurring-reservations-update/`;
    try {
      return this.http.put(url, i).pipe(catchError(this.handleError));
    } catch (error) {
      console.log(error);
    }
  }
  
  addRecurrenteList(reserva: ReservaRecurrente) {
    const i: iReservaRecurrente = {
      facility: reserva.facility,
      planner: reserva.planner ? reserva.planner : undefined,
      field_capacity: reserva.field_capacity,
      start_time: this.toJSONLocal(reserva.start_time),
      end_date: this.toJSONLocal(reserva.end_date),
      end_time: undefined,
      name: reserva.name,
      phone: reserva.phone,
      selected_weekdays: `${JSON.stringify(reserva.selected_weekdays)}`
    };

    const url = `${environment.apiUrl}/recurring-reservations-list/`;

    const resp = this.http.post(url, i).pipe(catchError(this.handleError))

    return resp;
  }

  updateReserva(reserva: Reserva){
    const i: iReserva = {
      facility: reserva.facility,
      planner:  undefined,
      field_capacity: reserva.field_capacity,
      start_time: this.toJSONLocal(reserva.start_time),
      end_time: this.toJSONLocal(reserva.end_time),
      name: reserva.name,
      phone: reserva.phone,
      reservation_type: reserva.reservation_type,
      deposit_paid:false,
      description: reserva.description ? reserva.description : undefined,
      ...(reserva.payway_payment !== null && { payment_variable: reserva.payway_payment })
    };
    
    const url = `${environment.apiUrl}/reservations/${reserva.id}/`;
    try {
      return this.http.put(url, i).pipe(catchError(this.handleError));
    } catch (error) {
      console.log(error);
    }
  }


  cancelReserva(id, data) {
    const url = `${environment.apiUrl}/reservations/${id}/cancel`;
    return this.http.put(url, data).pipe(catchError(this.handleError));
  }

  cancelReservaRecurrente(data: { id: number; }) {
    const headers = new HttpHeaders();
    const url = `${environment.apiUrl}/recurring-reservations-delete/`;
    const options = { headers: headers, body: { 'reservation_group': data.id } };

    return this.http.delete(url, options).pipe(catchError(this.handleError));
  }

  getGuests(id_reserva) {
    return this.http.get(
      `${environment.apiUrl}/reservations/${id_reserva}/guests/`
    );
  }

  getMatch(id_match): Observable<Match> {
    return this.http.get<Match>(`${environment.apiUrl}/matches/${id_match}/`);
  }

  private toJSONLocal(date) {
    const local = new Date(date);
    local.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return local.toJSON().slice(0, 19);
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      console.error(
        `Backend returned code ${error.status}, ` + `body was: ${error.error}`
      );
    }
    return throwError('Something bad happened; please try again later.');
  }

  setSharedCourrencyDay(value: any) {
    this.shareCurrencyDay = value;
  }

  getSharedCourrencyDay() {
    return this.shareCurrencyDay;
  }

  getSelectedWeekdays(idGroup: number): Observable<any> {
    const url = `${environment.apiUrl}/recurrent-reservations/list/selected_weekdays/${idGroup}/`;
    return from(this.http.get(url).toPromise()).pipe(
      catchError(error => {
        console.error("Ocurrió un error al obtener los datos:", error);
        throw error;
      })
    );
  }

}
